import React from 'react';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import CBMediaQuery from '../shared/CBMediaQuery';
import CBSpinnerFloating from '../shared/CBSpinnerFloating';
import {
  getStyle, SERVICE_CHAIN, SERVICE_FLEET, SERVICE_WORKSHOP,
} from '../../utils/utils';
import {
  fetchNotificationSettings as _fetchNotificationSettings,
  fetchNotificationSettingsUsers as _fetchNotificationSettingsUsers,
  updateNotificationSetting as _updateNotificationSetting,
  searchNotificationVehicles as _searchNotificationVehicles,
  clearNotificationVehicleSearch as _clearNotificationVehicleSearch,
} from '../../actions/notificationSettingsActions';
import NotificationCardHeader from './NotificationCardHeader';
import NotificationUserPicker from './NotificationUserPicker';
import NotificationOrganizationCard from './NotificationOrganizationCard';
import NotificationWorkshopCard from './NotificationWorkshopCard';
import NotificationChainCard from './NotificationChainCard';
import { styles, T } from './notificationSettingsStyles';

// The service names double as the API's portal names.
const SUPPORTED_SERVICES = [SERVICE_FLEET, SERVICE_WORKSHOP, SERVICE_CHAIN];

const ALL_ORGANIZATIONS = 'all';

// What the portal's top bar has selected, as API parameters, or null while the
// portal has not picked anything yet. The fleet's "all companies" is {}.
export function getSelection(props) {
  const {
    service, selectedOrganizationId, selectedWorkshopId, selectedChainId,
  } = props;
  if (service === SERVICE_FLEET) {
    if (selectedOrganizationId === null || selectedOrganizationId === undefined) {
      return null;
    }
    return selectedOrganizationId == ALL_ORGANIZATIONS ? {} : { organization_id: selectedOrganizationId };
  }
  if (service === SERVICE_WORKSHOP) {
    return selectedWorkshopId ? { workshop_id: selectedWorkshopId } : null;
  }
  if (service === SERVICE_CHAIN) {
    return selectedChainId ? { chain_id: selectedChainId } : null;
  }
  return null;
}

function selectionKey(selection) {
  return selection ? JSON.stringify(selection) : null;
}

// "Ilmoitukset": which e-mail notifications a user gets, in the fleet, service
// and chain portals, for the company / workshop / chain selected in the top
// bar. A pääkäyttäjä of that selection can switch to its people.
export class NotificationSettingsView extends React.Component {
  state = {
    selectedUserId: null,
  };

  componentDidMount() {
    this.load();
  }

  componentDidUpdate(prevProps) {
    // The top bar switched to another company / workshop / chain: start over
    // from the user's own settings there.
    if (selectionKey(getSelection(prevProps)) !== selectionKey(getSelection(this.props))) {
      this.setState({ selectedUserId: null });
      this.load();
    }
  }

  load() {
    const { service, fetchNotificationSettingsUsers, fetchNotificationSettings } = this.props;
    const selection = getSelection(this.props);
    if (SUPPORTED_SERVICES.indexOf(service) === -1 || !selection) {
      return;
    }
    fetchNotificationSettingsUsers(service, selection);
    fetchNotificationSettings(service, null, selection);
  }

  userChanged = (userId) => {
    const { service, fetchNotificationSettings } = this.props;
    this.setState({ selectedUserId: userId });
    fetchNotificationSettings(service, userId, getSelection(this.props));
  };

  toggle = (targetType, targetId, enabled) => {
    const { service, updateNotificationSetting } = this.props;
    updateNotificationSetting(
      service, this.state.selectedUserId, getSelection(this.props), targetType, targetId, enabled,
    );
  };

  search = (organizationId, text) => {
    const { service, searchNotificationVehicles } = this.props;
    searchNotificationVehicles(service, this.state.selectedUserId, organizationId, text);
  };

  renderSections(screenSize) {
    const {
      t, service, notificationSettings, clearNotificationVehicleSearch,
    } = this.props;
    const { settings, saving, vehicleSearch } = notificationSettings;

    const organizations = settings.organizations || [];
    const workshops = settings.workshops || [];
    const chains = settings.chains || [];

    if (!organizations.length && !workshops.length && !chains.length) {
      return (
        <div style={getStyle(screenSize, styles, 'card')} data-testid='notification-empty'>
          <p style={getStyle(screenSize, styles, 'emptyText')}>{t('nothingToManage')}</p>
        </div>
      );
    }

    return (
      <React.Fragment>
        {organizations.map(organization => (
          <NotificationOrganizationCard
            key={`${settings.user.id}-${organization.id}`}
            organization={organization}
            disabled={saving}
            vehicleSearch={vehicleSearch}
            onToggle={this.toggle}
            onSearch={this.search}
            onClearSearch={clearNotificationVehicleSearch}
          />
        ))}
        {workshops.length > 0 && (
          <NotificationWorkshopCard workshops={workshops} disabled={saving} onToggle={this.toggle} />
        )}
        {chains.length > 0 && (
          <NotificationChainCard chains={chains} service={service} disabled={saving} onToggle={this.toggle} />
        )}
      </React.Fragment>
    );
  }

  render() {
    const {
      t, service, notificationSettings, compact,
    } = this.props;
    const {
      settings, loading, error, users,
    } = notificationSettings;

    if (SUPPORTED_SERVICES.indexOf(service) === -1) {
      return null;
    }

    const selection = getSelection(this.props);
    const allCompanies = service === SERVICE_FLEET && !!selection && !selection.organization_id;

    return (
      <CBMediaQuery>
        {screenSize => (
          <div style={{ ...getStyle(screenSize, styles, 'page'), ...(compact ? { padding: 0 } : {}) }}>
            <div style={getStyle(screenSize, styles, 'card')}>
              <NotificationCardHeader
                icon='pi pi-bell'
                title={t('pageTitle')}
                description={t('pageDescription')}
              />
            </div>

            {allCompanies && (
              <div style={getStyle(screenSize, styles, 'banner')} data-testid='all-companies-note'>
                <i className='pi pi-info-circle' />
                <span>{t('allCompaniesNote')}</span>
              </div>
            )}

            <NotificationUserPicker
              users={users}
              selectedUserId={this.state.selectedUserId}
              onChange={this.userChanged}
            />

            {settings && settings.user && !settings.user.is_self && (
              <div style={getStyle(screenSize, styles, 'banner')} data-testid='editing-other-user'>
                <i className='pi pi-user-edit' />
                <span>{t('editingOtherUser', { name: settings.user.name || settings.user.email })}</span>
              </div>
            )}

            {loading && (
              <div style={{ position: 'relative', minHeight: 120 }}>
                <CBSpinnerFloating color={T.accent} />
              </div>
            )}

            {!loading && error && (
              <div style={getStyle(screenSize, styles, 'card')}>
                <p style={getStyle(screenSize, styles, 'emptyText')}>{t('loadError')}</p>
              </div>
            )}

            {selection && !loading && !error && settings && this.renderSections(screenSize)}
          </div>
        )}
      </CBMediaQuery>
    );
  }
}

function mapStateToProps(state) {
  return {
    service: state.app.service,
    selectedOrganizationId: state.organization.selectedOrganization,
    selectedWorkshopId: state.workshop.selectedWorkshop,
    selectedChainId: state.chain.selectedChainId,
    notificationSettings: state.notificationSettings,
  };
}

export default connect(
  mapStateToProps,
  {
    fetchNotificationSettings: _fetchNotificationSettings,
    fetchNotificationSettingsUsers: _fetchNotificationSettingsUsers,
    updateNotificationSetting: _updateNotificationSetting,
    searchNotificationVehicles: _searchNotificationVehicles,
    clearNotificationVehicleSearch: _clearNotificationVehicleSearch,
  },
)(translate('NotificationSettings')(NotificationSettingsView));
